import type { Messages } from "@/i18n/locales/en";
import { legalMessagesNl } from "@/i18n/locales/legalNlData";
import { pagesMessagesNl } from "@/i18n/locales/pagesNlData";

export const nlMessages = {
  meta: {
    description:
      "WasOK — scan producten, sla beoordelingen en notities op, onthoud wat het kopen waard was.",
    title: "WasOK — Onthoud wat het kopen waard was",
  },
  header: {
    navFeatures: "Functies",
    navHowItWorks: "Hoe het werkt",
    navPrivacy: "Privacy",
    navUseCases: "Toepassingen",
    download: "Downloaden",
    openMenu: "Menu openen",
    closeMenu: "Menu sluiten",
    languageAria: "Taal",
  },
  hero: {
    badge: "📱 Nu beschikbaar op Android",
    title: "Onthoud wat het kopen waard was.",
    subtitle:
      "Scan producten, sla beoordelingen en notities op, en weet direct of een product écht goed was.",
    googlePlay: "Downloaden via Google Play",
    watchDemo: "Demo bekijken",
    privacyLine: "Alle gegevens blijven op je apparaat.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Scan om te onthouden",
    snackCard: "Heerlijk!",
    coffeeCard: "Te bitter",
    scrollHint: "Scroll om te ontdekken",
  },
  problems: {
    heading: "Komt dit bekend voor?",
    subheading:
      "We kennen het allemaal — voor het schap staan en proberen te herinneren of het vorige keer lekker was.",
    notedAgo: "✓ Je hebt dit 3 maanden geleden genoteerd",
    items: [
      {
        question: "Welke chocolade vond ik écht lekker?",
        note: "Perfecte zoetheid",
      },
      {
        question: "Waarom koop ik deze shampoo niet meer?",
        note: "Droogde mijn haar uit",
      },
      {
        question: "Was deze koffie goed of tegenvallend?",
        note: "Te bitter",
      },
    ],
  },
  howItWorks: {
    heading: "Hoe het werkt",
    subheading:
      "Vier eenvoudige stappen om je productmeningen nooit meer te vergeten.",
    stepPrefix: "Stap",
    steps: [
      {
        title: "Scan elke productbarcode",
        description:
          "Snel en eenvoudig scannen met je camera. Werkt met elke barcode.",
      },
      {
        title: "Sla beoordelingen, opmerkingen & foto's op",
        description:
          "Beoordeel producten, voeg notities toe, voeg foto's toe en noteer prijzen en locaties.",
      },
      {
        title: "Herinner je ervaring direct",
        description:
          "Scan hetzelfde product later en zie je eerdere beoordeling en notities.",
      },
      {
        title: "Maak slimme boodschappenlijsten",
        description:
          "Maak boodschappenlijsten van producten die je al vertrouwt en leuk vindt.",
      },
    ],
  },
  privacy: {
    heading: "Je winkelgeschiedenis blijft van jou.",
    body: "WasOK helpt je producten te onthouden die je hebt geprobeerd, terwijl beoordelingen, notities, foto's en boodschappenlijsten lokaal op je apparaat worden opgeslagen.",
    trustBanner:
      "💚 Gebouwd rond je winkelgeschiedenis — niet rond het verzamelen van je gegevens.",
    bullets: [
      "Geen account vereist",
      "Geen cloudsynchronisatie vereist",
      "Lokale opslag eerst",
      "Back-ups onder jouw controle",
      "Optionele barcode-opzoeking voor productinformatie.",
      "Ondersteund door advertenties via Google AdMob",
    ],
  },
  features: {
    heading: "Alles wat je nodig hebt",
    subheading:
      "Krachtige functies om producttracking eenvoudig en privé te maken.",
    items: [
      {
        title: "Barcodescanner",
        description: "Snel en nauwkeurig barcodes scannen voor elk product.",
      },
      {
        title: "Productbeoordelingen",
        description: "Beoordeel producten met 1–5 sterren om kwaliteit te onthouden.",
      },
      {
        title: "Productfoto's",
        description: "Voeg foto's toe om verpakking of details te onthouden.",
      },
      {
        title: "Categorieën",
        description: "Organiseer producten in eigen categorieën.",
      },
      {
        title: "Winkeltracking",
        description: "Onthoud waar je elk product hebt gekocht.",
      },
      {
        title: "Prijsregistratie",
        description: "Volg prijzen om de beste deals te vinden.",
      },
      {
        title: "Boodschappenlijsten",
        description: "Maak lijsten van je favoriete producten.",
      },
      {
        title: "Offline toegang",
        description: "Werkt perfect zonder internetverbinding.",
      },
      {
        title: "Lokale opslag",
        description: "Al je gegevens veilig opgeslagen op je apparaat.",
      },
    ],
  },
  useCases: {
    heading: "Perfect voor elk schap",
    subheading:
      "Of je nu winkelt voor eten, beauty of huishouden — WasOK helpt je te onthouden.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks & snoep",
        description: "Onthoud welke snoepjes écht lekker waren.",
      },
      {
        emoji: "☕",
        title: "Koffie & dranken",
        description: "Volg koffies die het waard zijn om opnieuw te kopen.",
      },
      {
        emoji: "💄",
        title: "Cosmetica & huidverzorging",
        description: "Houd notities bij over huidreacties.",
      },
      {
        emoji: "🧹",
        title: "Huishoudelijke artikelen",
        description: "Vermijd producten die tegenvielen opnieuw te kopen.",
      },
    ],
  },
  screenshots: {
    heading: "Mooi & intuïtief",
    subheading:
      "Een doordacht ontworpen interface die producttracking tot een plezier maakt.",
    items: [
      { title: "Scanner" },
      { title: "Productdetail" },
      { title: "Beoordeling invoeren" },
      { title: "Zoeken & geschiedenis" },
      { title: "Boodschappenlijst" },
    ],
  },
  cta: {
    heading: "Winkelgeheugen voor het echte leven.",
    body: "Stop met vergeten welke producten het kopen waard waren. Begin vandaag slimmer te winkelen.",
    googlePlay: "Downloaden via Google Play",
    scanQr: "Scan om te downloaden",
    tagline: "Privé · Offline-eerst · Eenvoudig",
  },
  footer: {
    tagline: "Onthoud wat het kopen waard was.",
    linksHeading: "Links",
    privacyPolicy: "Privacybeleid",
    terms: "Servicevoorwaarden",
    contact: "Contact",
    linkFaq: "FAQ",
    linkLicenses: "Open-sourcelicenties",
    linkAdvertising: "Advertenties & monetisatie",
    downloadHeading: "Downloaden",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Alle rechten voorbehouden. Gemaakt met 💚 voor slimmer winkelen.",
  },
  legal: legalMessagesNl,
  faq: pagesMessagesNl.faq,
  support: pagesMessagesNl.support,
  advertising: pagesMessagesNl.advertising,
} satisfies Messages;
